import { useMemo } from "react";

import { useCurrentUser } from "@/hooks/use-current-user";
import { useMyBusinesses } from "@/hooks/use-businesses";
import { useRumIdentity } from "@/hooks/use-rum-identity";
import type { RumUserContext } from "@/lib/observability-user";

// ADV-1319: a render-less sync that feeds the authenticated user + their
// account/business context into the Dash0 RUM SDK. Mounted once inside the auth
// + query providers. Assembles immutable ids + roles from the JWT
// (useCurrentUser) and the active business name + slug from the already-cached
// /core/me/businesses query (useMyBusinesses) — no extra network cost.
export function RumIdentitySync(): null {
  const currentUser = useCurrentUser();
  // Gate on an authenticated user: this component mounts ABOVE the router, so an
  // ungated fetch would hit the bearer-protected /core/me/businesses on public
  // routes (login/signup/callback) and trigger a 401 signOut()+redirect loop.
  // When enabled, it reuses the cache the business switcher already populates.
  const { data: businesses } = useMyBusinesses(!!currentUser);

  const context = useMemo<RumUserContext | null>(() => {
    if (!currentUser) return null;
    const active = businesses?.businesses.find(
      (b) => b.id === currentUser.businessId,
    );
    return {
      userId: currentUser.userId,
      displayName: currentUser.displayName,
      accountId: currentUser.accountId,
      businessId: currentUser.businessId,
      businessName: active?.name,
      businessSlug: active?.slug,
      systemRole: currentUser.systemRole,
      endUserRole: currentUser.endUserRole,
      accountRole: currentUser.accountRole,
      businessRole: currentUser.businessRole,
    };
  }, [currentUser, businesses]);

  useRumIdentity(context);
  return null;
}
